import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from '../../../shared/services/toast.service';
import { finalize, first, merge } from 'rxjs';
import { Store } from '@ngrx/store';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ApiService } from '../../../shared/services/api.service';
import * as AppSelectors from '../../../store/app/app.selectors';
import { AppState } from 'src/app/store/app/app.state';
import { LATE_ERROR_STATE } from '../../../shared/helpers/error-state';
import { sha256Hex } from '../../../shared/helpers/sha256';
import { tooManyAttempts } from '../../../shared/helpers/sign-in-wait';

type ResetField = 'username' | 'oldPassword' | 'newPassword' | 'confirmedNewPassword';

// The login refused an expired password: the user changes it here without a
// session. Like the profile dialog, the page waits for the answer, shows each
// error next to its field and leaves for the login only once it succeeded.
@Component({
    selector: 'urban-reset-expired',
    templateUrl: './reset-expired.component.html',
    styleUrls: ['./reset-expired.component.scss'],
    standalone: false,
    providers: [LATE_ERROR_STATE]
})
export class ResetExpiredComponent implements OnInit {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly appStore = inject(Store<AppState>);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  public form: UntypedFormGroup;
  public error: { key: string, params?: Record<string, string> } | null = null;
  public saving: boolean = false;
  public hideOldPassword: boolean = true;
  public hideNewPassword: boolean = true;
  public hideConfirmedNewPassword: boolean = true;
  private readonly passwordRule: RegExp = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
  public icons: Record<string, SafeHtml> = {};

  ngOnInit(): void {
    this.form = this.fb.group({
      username: ['', Validators.required],
      oldPassword: ['', Validators.required],
      newPassword: ['', Validators.required],
      confirmedNewPassword: ['', Validators.required]
    });

    // One answer for the pair: editing either field clears it on both.
    merge(this.form.controls.username.valueChanges, this.form.controls.oldPassword.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (['username', 'oldPassword'] as ResetField[]).forEach(field => this.unflag(field, 'wrongCredentials')));

    this.appStore.select(AppSelectors.getIcons).pipe(first()).subscribe(response => {
      response.filter(icon => ['visibility-outline', 'visibility-off-outline'].includes(icon.Name))
        .forEach(icon => {
          this.icons[icon.Name] = this.sanitizer.bypassSecurityTrustHtml(icon.Content);
        });
    });
  }

  public back(): void {
    void this.router.navigateByUrl('auth/login');
  }

  public async update(): Promise<void> {
    if (this.saving) {
      return;
    }

    this.error = null;
    Object.values(this.form.controls).forEach(control => control.updateValueAndValidity());

    const { username, oldPassword, newPassword, confirmedNewPassword } = this.form.getRawValue();

    if (!username?.trim() || !oldPassword || !newPassword || !confirmedNewPassword) {
      this.error = { key: 'CHANGE_PASSWORD_DIALOG.ERROR_EMPTY' };
      return;
    }
    if (!this.passwordRule.test(newPassword)) {
      this.flag('newPassword', 'rule');
      return;
    }
    if (newPassword !== confirmedNewPassword) {
      this.flag('confirmedNewPassword', 'mismatch');
      return;
    }
    if (newPassword === oldPassword) {
      this.flag('newPassword', 'same');
      return;
    }

    this.saving = true;
    const [oldHash, newHash] = await Promise.all([sha256Hex(oldPassword), sha256Hex(newPassword)]);

    this.apiService.changePassword({ username: username.trim(), oldPassword: oldHash, newPassword: newHash, confirmedNewPassword: newHash })
      .pipe(
        finalize(() => this.saving = false),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.toast.done('RESET_EXPIRED.SUCCESS');
          this.back();
        },
        error: (err: HttpErrorResponse) => this.showServerError(err)
      });
  }

  // Zesty checks the user and the old password together and says only that
  // they do not match, so both fields are marked.
  private showServerError(err: HttpErrorResponse): void {
    const message: unknown = err?.error?.Message;

    if (err?.status === 401 && message === 'Wrong password') {
      this.flag('username', 'wrongCredentials');
      this.flag('oldPassword', 'wrongCredentials');
    }
    // The sign-in lock covers this page too: same words as the login page.
    else if (err?.status === 429) {
      this.error = tooManyAttempts(err);
    }
    else if (message === 'Old and new password are the same') {
      this.flag('newPassword', 'same');
    }
    else {
      this.error = { key: err?.status === 0 ? 'ERROR.OFFLINE' : 'ERROR.SOMETHING_WRONG' };
    }
  }

  // Cleared by the field's own validators as soon as it is edited.
  private flag(field: ResetField, error: string): void {
    const control = this.form.controls[field];
    control.setErrors({ ...(control.errors ?? {}), [error]: true });
    control.markAsTouched();
  }

  private unflag(field: ResetField, error: string): void {
    const control = this.form.controls[field];

    if (control.hasError(error)) {
      const { [error]: _, ...rest } = control.errors!;
      control.setErrors(Object.keys(rest).length ? rest : null);
    }
  }

  public changeVisibility(field: 'old' | 'new' | 'confirmed'): void {
    switch (field) {
      case 'old':
        this.hideOldPassword = !this.hideOldPassword
        break;
      case 'new':
        this.hideNewPassword = !this.hideNewPassword
        break;
      case 'confirmed':
        this.hideConfirmedNewPassword = !this.hideConfirmedNewPassword
        break;
    }
  }
}
